import React, { useId, useState } from 'react';
import { useIntl } from 'react-intl';
import { Box, Collapse, List, ListItemButton, useTheme } from '@mui/material';
import ArrowDown from '@mui/icons-material/KeyboardArrowDownRounded';
import RouteListItem from './RouteListItem';
import { routeItemProps } from './utils';

/**
 * A first-level folder as the macro area it is: an uppercase caption with a caret, and its routes
 * beneath it as ordinary items.
 *
 * The caption is not an item - no pill, no ripple, nothing to navigate to - because a macro area is
 * a heading over what follows rather than somewhere to go. The caret is what makes it a control, and
 * it stays put when the area is closed, so a closed area still says it is there and still opens.
 *
 * Its routes hang off a guide line dropped from the caption's first letter, as a nested folder's
 * hang off its icon, so what belongs to a folder reads the same at every level.
 *
 * @param {Object} props - Component props.
 * @param {String} props.routeName - The folder's key, which is what the caption is translated from.
 * @param {Array} props.routes - The routes it holds.
 * @param {String} props.selected - The current location, for the item that matches it.
 * @param {Function} props.onClick - Receives the path of the route chosen.
 * @param {Boolean} props.sidebarOpen - Whether the sidebar is expanded.
 * @param {Boolean} props.first - Whether it is the first area, which opens no boundary above itself.
 * @param {Boolean} props.defaultOpen - Whether it starts open.
 *
 * @returns {React.JSX.Element}
 */
const SidebarSection = function SidebarSection({
  routeName,
  routes = [],
  selected,
  onClick,
  sidebarOpen,
  first = false,
  defaultOpen = true,
}) {
  const intl = useIntl();
  const theme = useTheme();
  const id = useId();
  const [open, setOpen] = useState(defaultOpen);

  // Folder names are server-driven and may still arrive as an untranslated GUID.
  const label = intl.messages[routeName] ?? routeName;

  return (
    <Box component="li" sx={{ display: 'block' }}>
      {sidebarOpen ? (
        <ListItemButton
          aria-expanded={open}
          aria-controls={`${id}-content`}
          onClick={() => setOpen(!open)}
          disableRipple
          sx={{
            gap: '8px',
            padding: '16px 18px 6px',
            lineHeight: 1,
            color: theme.palette.text.secondary,
            '&:hover': {
              backgroundColor: 'transparent',
              color: theme.palette.text.primary,
            },
          }}
        >
          <Box
            component="span"
            id={`${id}-label`}
            sx={{
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
              fontSize: '12px',
              fontWeight: 700,
              letterSpacing: '.09em',
              textTransform: 'uppercase',
            }}
          >
            {label}
          </Box>
          <ArrowDown
            sx={{
              fontSize: '18px',
              opacity: 0.7,
              flex: 'none',
              transition: 'transform .2s ease',
              transform: `rotate(${open ? 0 : -90}deg)`,
            }}
          />
        </ListItemButton>
      ) : (
        // The rail has no room for a caption, so an area is marked by the space and the line above
        // it. The first needs none: nothing precedes it to be told apart from.
        !first && (
          <Box
            role="presentation"
            sx={{
              height: '1px',
              margin: '12px 14px',
              backgroundColor: theme.palette.divider,
            }}
          />
        )
      )}
      {/* The rail has no caption to reopen from, so an area closed while the sidebar was open must
          not strand its routes there. */}
      <Collapse in={!sidebarOpen || open} id={`${id}-content`}>
        <List
          disablePadding
          aria-labelledby={sidebarOpen ? `${id}-label` : undefined}
          sx={
            sidebarOpen
              ? { ml: '18px', borderLeft: `1px solid ${theme.palette.divider}` }
              : undefined
          }
        >
          {routes.map((route) => (
            <RouteListItem
              key={route.id}
              {...routeItemProps(route)}
              selected={selected}
              onClick={onClick}
              sidebarOpen={sidebarOpen}
            />
          ))}
        </List>
      </Collapse>
    </Box>
  );
};

export default SidebarSection;
